import { sendAnalytics } from '@audio-network/analytics'
import { Divider, Skeleton, Text } from '@audio-network/componentlibrary'
import { NewTab } from '@audio-network/componentlibrary/icons'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'

import { clientGetTrackFeaturedIn } from '@/client/track/featuredIn'
import Link from '@/components/shared/Link'
import { ALBUM_ARTWORK_S } from '@/constants/sizing'
import { useOnScreen } from '@/hooks/useOnScreen'
import optimizeContentfulImage from '@/utils/contentful/optimizeContentfulImage'
import { getAlbumUrl } from '@/utils/urls'

import styles from './TrackFeaturedIn.module.css'
import { TrackFeaturedInProps } from './TrackFeaturedIn.types'

const ARTWORK_WIDTH = 64

const TrackFeaturedIn = ({ track }: TrackFeaturedInProps): JSX.Element => {
	const featuredInRef = useRef<HTMLDivElement>(null)
	const { locale } = useRouter()
	const [featuredIn, setFeaturedIn] = useState<{
		album: Album
		collection: Collection
		playlist: Playlist
	}>({ album: track.album, collection: undefined, playlist: undefined })

	const isOnScreen = useOnScreen(featuredInRef, {
		once: true,
		// Enough to preload on tray opening
		rootMargin: '160px 0px 0px 0px',
	})

	const { t } = useTranslation(['app'])

	useEffect(() => {
		const fetchFeaturedIn = async (): Promise<void> => {
			const moreFeaturedIn = await clientGetTrackFeaturedIn(track, locale)

			setFeaturedIn({ ...featuredIn, ...moreFeaturedIn })
		}

		if (
			isOnScreen &&
			typeof featuredIn.collection === 'undefined' &&
			typeof featuredIn.playlist === 'undefined'
		) {
			fetchFeaturedIn()
		}
	}, [isOnScreen, locale, track.album?.number, track, track.id, featuredIn])

	const handleClick = (target: 'album' | 'collection' | 'playlist'): void => {
		switch (target) {
			case 'album':
				sendAnalytics({
					noun: 'Album',
					pastTenseVerb: 'Clicked',
					eventParameters: {
						AlbumNo: featuredIn.album.number,
						AlbumTitle: featuredIn.album.name,
					},
				})
				break
			case 'collection':
				sendAnalytics({
					noun: 'Collection',
					pastTenseVerb: 'Clicked',
					eventParameters: {
						Name: featuredIn.collection.title,
						UrlSlug: featuredIn.collection.id,
					},
				})
				break
			case 'playlist':
				sendAnalytics({
					noun: 'Playlist',
					pastTenseVerb: 'Clicked',
					eventParameters: {
						PlaylistTitle: featuredIn.playlist.title,
						UrlSlug: featuredIn.playlist.urlSlug,
					},
				})
				break
		}
	}

	return (
		<div ref={featuredInRef} className={styles.featuredInContainer}>
			{featuredIn?.album && (
				<Link
					href={getAlbumUrl(featuredIn.album)}
					target="_blank"
					rel="noreferrer"
					className={styles.link}
					onClick={() => handleClick('album')}
				>
					{featuredIn.album.artworkImageUrl && (
						<Image
							src={optimizeContentfulImage(featuredIn.album.artworkImageUrl, {
								width: ALBUM_ARTWORK_S,
								height: ALBUM_ARTWORK_S,
							})}
							alt=""
							width={ARTWORK_WIDTH}
							height={ARTWORK_WIDTH}
							className={styles.artwork}
						/>
					)}
					<div className={styles.details}>
						<Text bold variant="bodyS">
							{featuredIn.album.name}
						</Text>
						<Text variant="bodyS">{t('album')}</Text>
					</div>
					<NewTab />
				</Link>
			)}

			{typeof featuredIn.collection === 'undefined' && (
				<>
					<Divider className={styles.divider} />
					<div className={styles.link}>
						<Skeleton
							variant="rounded"
							width={ARTWORK_WIDTH}
							height={ARTWORK_WIDTH}
						/>
						<div className={styles.details}>
							<Skeleton width={100} />
							<Skeleton width={40} />
						</div>
					</div>
				</>
			)}

			{featuredIn.collection && (
				<>
					<Divider className={styles.divider} />
					<Link
						href={`/collections/${featuredIn.collection.id}`}
						target="_blank"
						rel="noreferrer"
						className={styles.link}
						onClick={() => handleClick('collection')}
					>
						{featuredIn.collection.thumbnailSquare?.url && (
							<Image
								src={optimizeContentfulImage(
									featuredIn.collection.thumbnailSquare?.url,
									{ width: ALBUM_ARTWORK_S, height: ALBUM_ARTWORK_S }
								)}
								alt=""
								width={ARTWORK_WIDTH}
								height={ARTWORK_WIDTH}
								className={styles.artwork}
							/>
						)}
						<div className={styles.details}>
							<Text bold variant="bodyS">
								{featuredIn.collection.title}
							</Text>
							<Text variant="bodyS">{t('collection')}</Text>
						</div>
						<NewTab />
					</Link>
				</>
			)}

			{typeof featuredIn.playlist === 'undefined' && (
				<>
					<Divider className={styles.divider} />
					<div className={styles.link}>
						<Skeleton
							variant="rounded"
							width={ARTWORK_WIDTH}
							height={ARTWORK_WIDTH}
						/>
						<div className={styles.details}>
							<Skeleton width={100} />
							<Skeleton width={40} />
						</div>
					</div>
				</>
			)}

			{featuredIn.playlist && (
				<>
					<Divider className={styles.divider} />
					<Link
						href={`/playlist/${featuredIn.playlist.urlSlug}`}
						target="_blank"
						rel="noreferrer"
						className={styles.link}
						onClick={() => handleClick('playlist')}
					>
						{featuredIn.playlist.thumbnail?.url && (
							<Image
								src={optimizeContentfulImage(
									featuredIn.playlist.thumbnail?.url,
									{ width: ALBUM_ARTWORK_S, height: ALBUM_ARTWORK_S }
								)}
								alt=""
								width={ARTWORK_WIDTH}
								height={ARTWORK_WIDTH}
								className={styles.artwork}
							/>
						)}
						<div className={styles.details}>
							<Text bold variant="bodyS">
								{featuredIn.playlist.title}
							</Text>
							<Text variant="bodyS">{t('playlist')}</Text>
						</div>
						<NewTab />
					</Link>
				</>
			)}
		</div>
	)
}

export default TrackFeaturedIn
